import { UnexpectedError } from '@adv-frontend/error';

export interface RateLimiterOptions {
    // Интервал сброса подсчета вызовов функций
    interval: number;
    // Количество вызовов функций, которое должно вызывать ошибку
    threshold: number;
    // Количество тиков (interval) для сброса антифлапа
    antiflapTickLimit: number;
    // Количество ошибок, которые мы считаем за флапы за количество antiflapTickLimit
    antiflapLimit: number;
    // Интервал между ошибками
    errorInterval: number;
    // Компоненты, для которых нормально рендериться много раз (например, ячейка грида)
    // Для них будем смотреть только на трендовость — резкий скачок ошибок
    manyRendersComponentsList: string[];
}

interface FunctionWithDisplayName extends Function {
    displayName?: string;
}

interface ComponentAntiflap {
    renders: number[];
    startTick: number;
    antiflap: number;
}

/*
Класс считает вызовы обернутых функций и выбрасывает ошибку,
если количество вызовов превышает threshold antiflap раз за antiflapTickLimit интервалов
Функции добавляются к подсчету через метод wrap
*/
export class RateLimiter {
    private rendersMap: WeakMap<Function, number>;
    private antiflapMap: WeakMap<Function, ComponentAntiflap>;

    private interval: number;
    private threshold: number;

    private antiflapTickLimit: number;
    private antiflapFreeTicks: number;
    private antiflapLimit: number;
    private antiflapTick: number;
    private antiflapBlocker: boolean;

    private manyRendersComponentsList: string[];

    private errorInterval: number;
    private errorTimeout: null | ReturnType<typeof setTimeout>;

    private clearIntervalTimeout: null | ReturnType<typeof setTimeout>;

    constructor({
        threshold,
        interval,
        antiflapTickLimit,
        antiflapLimit,
        errorInterval,
        manyRendersComponentsList,
    }: RateLimiterOptions) {
        this.rendersMap = new Map();
        this.antiflapMap = new WeakMap();

        this.interval = interval;
        this.threshold = threshold;

        this.antiflapTickLimit = antiflapTickLimit;
        this.antiflapFreeTicks = antiflapTickLimit - antiflapLimit;
        this.antiflapTick = 0;
        this.antiflapLimit = antiflapLimit;
        this.antiflapBlocker = false;

        this.manyRendersComponentsList = manyRendersComponentsList;

        this.errorInterval = errorInterval;
        this.errorTimeout = null;

        this.clearIntervalTimeout = null;
        this.initClearInterval();
    }

    private clearRenders() {
        this.rendersMap = new WeakMap();
        this.antiflapBlocker = false;
        this.antiflapTick++;
    }

    private initClearInterval() {
        const timeoutFn = () => {
            this.clearRenders();
            this.clearIntervalTimeout = setTimeout(timeoutFn, this.interval);
        };
        timeoutFn();
    }
    private isObject(object: unknown): object is object {
        return typeof object === 'object' && object !== null;
    }

    private isComponent(type: unknown): type is FunctionWithDisplayName {
        return typeof type === 'function';
    }

    private isHOC(type: unknown): type is { render: FunctionWithDisplayName } {
        return this.isObject(type) && 'render' in type && typeof type.render === 'function';
    }

    private isMemo(type: unknown): type is { type: unknown } {
        return this.isObject(type) && 'type' in type;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- автоматический игнор в рамках DIRECT-265451. Исправьте при следующем редактировании при помощи type-assert, чтобы избежать возможных ложно-положительных проверок typecheck!
    // @ts-expect-error DIRECT-235591 TS7030
    private findComponentFunction(type: unknown): FunctionWithDisplayName | undefined {
        const endType = this.isMemo(type) ? type.type : type;

        if (this.isComponent(endType)) {
            return endType;
        } else if (this.isHOC(endType)) {
            return endType.render;
        }
    }

    private check(type: unknown, props?: unknown, key?: unknown) {
        if (this.errorTimeout) {
            return;
        }

        const component = this.findComponentFunction(type);

        if (!component) {
            return;
        }

        const currentComponentRenders = (this.rendersMap.get(component) ?? 0) + 1;
        this.rendersMap.set(component, currentComponentRenders);

        if (currentComponentRenders > this.threshold) {
            let currentAntiflap: ComponentAntiflap;
            const antiflapFromMap = this.antiflapMap.get(component);

            if (
                !antiflapFromMap ||
                this.antiflapFreeTicks <
                    this.antiflapTick - antiflapFromMap.startTick - antiflapFromMap.renders.filter(Boolean).length
            ) {
                currentAntiflap = {
                    renders: [],
                    startTick: this.antiflapTick,
                    antiflap: this.antiflapLimit,
                };
            } else {
                currentAntiflap = antiflapFromMap;
            }

            currentAntiflap.renders[this.antiflapTick - currentAntiflap.startTick] = currentComponentRenders;

            if (!this.antiflapBlocker) {
                if (currentAntiflap.antiflap) {
                    currentAntiflap.antiflap--;
                    this.antiflapBlocker = true;
                } else {
                    const componentName = component.displayName || component.name;

                    // Если нет componentName или он с коротким названием, то это минифицированное название:
                    // логируем общо, чтобы не плодить ошибки на каждый релиз
                    const isUnknownComponent = !componentName || componentName.length <= 2;
                    const loggedComponentName = isUnknownComponent ? 'unknown component' : `component ${componentName}`;

                    const error = new UnexpectedError(
                        `Exceeded render limit: ${this.antiflapLimit} out of ${this.antiflapTickLimit} intervals exceeded ${this.threshold} renders per ${this.interval}ms threshold in ${loggedComponentName}.`,
                        {
                            cause: undefined,
                            additional: {
                                component: componentName,
                                props: this.isObject(props) && Object.keys(props),
                                key: typeof key === 'string' && key,
                                renders: currentAntiflap.renders,
                            },
                        }
                    );

                    const logError = this.manyRendersComponentsList.includes(componentName)
                        ? console.warn
                        : console.error;
                    logError(error);

                    this.errorTimeout = setTimeout(() => (this.errorTimeout = null), this.errorInterval);
                }
            }

            this.antiflapMap.set(component, currentAntiflap);
        }
    }

    wrap<Fn extends (...args: never[]) => void>(func: Fn) {
        const limiter = this;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
        return function (this: ThisParameterType<Fn>, ...args: Parameters<Fn>) {
            limiter.check(args[0], args[1], args[2]);

            return func.apply(this, args);
        } as Fn;
    }

    kill() {
        if (this.clearIntervalTimeout) {
            clearTimeout(this.clearIntervalTimeout);
        }
    }
}
